import React, { createContext, useContext, useEffect, useState } from 'react';
import { createBrowserSupabaseClient } from '../lib/supabase-client';
import { API_BASE } from '../lib/api-client';

const AuthContext = createContext(undefined);

/**
 * Global AuthProvider for the React frontend (CRA).
 * Manages the Supabase session and user state.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAdminLoading, setIsAdminLoading] = useState(true);

  const [supabase] = useState(() => createBrowserSupabaseClient());

  useEffect(() => {
    if (!supabase) {
      setIsLoading(false);
      setIsAdminLoading(false);
      return;
    }

    let active = true;

    const loadAccount = async (currentSession) => {
      if (!currentSession?.access_token) {
        if (active) {
          setProfile(null);
          setIsAdmin(false);
          setIsAdminLoading(false);
        }
        return;
      }

      if (active) setIsAdminLoading(true);
      try {
        const response = await fetch(`${API_BASE}/auth/me`, {
          headers: { Authorization: `Bearer ${currentSession.access_token}` },
        });
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.error || 'Account access could not be loaded.');
        if (active) {
          setProfile(payload.profile || null);
          setIsAdmin(payload.isAdmin === true);
        }
      } catch (error) {
        console.error('Error fetching account access:', error);
        if (active) {
          setProfile(null);
          setIsAdmin(false);
        }
      } finally {
        if (active) setIsAdminLoading(false);
      }
    };

    // 1. Initial session check
    const fetchSession = async () => {
      try {
        const { data: { session: initialSession }, error } = await supabase.auth.getSession();
        if (error) throw error;
        
        setSession(initialSession);
        setUser(initialSession?.user ?? null);
        await loadAccount(initialSession);
      } catch (err) {
        console.error('Error fetching initial session:', err);
        setIsAdmin(false);
        setIsAdminLoading(false);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSession();

    // 2. Listen for auth changes (Login, Logout, Token Refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, currentSession) => {
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        setIsLoading(false);
        void loadAccount(currentSession);
      }
    );

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, [supabase]);

  const updateProfile = async ({ fullName }) => {
    if (!session?.access_token) throw new Error('Authentication required.');

    const response = await fetch(`${API_BASE}/auth/me`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ full_name: fullName }),
    });
    const payload = await response.json();
    if (!response.ok) throw new Error(payload.error || 'Profile could not be updated.');

    setProfile(payload.profile || null);
    return payload.profile;
  };

  const value = {
    user,
    profile,
    session,
    isLoading,
    isAdmin,
    isAdminLoading,
    signOut: () => supabase?.auth.signOut(),
    updateProfile,
    getAccessToken: () => session?.access_token || null,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Standard hook for consuming the auth state.
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an <AuthProvider>');
  }
  return context;
}
